import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Map } from "lucide-react";

const fmtTicket = (v) => {
  if (!v) return null;
  if (v >= 1000000) return `$${(v / 1000000).toFixed(v % 1000000 ? 1 : 0)}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${v}`;
};

export const InvestorMapPanel = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let on = true;
    api.get(`/studio/portfolio/${companyId}/investor-map`)
      .then(({ data }) => on && setData(data))
      .catch(() => on && setError("Could not load the investor map"));
    return () => { on = false; };
  }, [companyId]);

  if (error) return <p className="mt-4 text-sm text-rose-400">{error}</p>;
  if (!data) return <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Mapping mandate-matched funders…</div>;

  const { made, target_min, target_max } = data.intro_target;
  const pct = Math.min(100, Math.round((made / target_min) * 100));

  return (
    <div className="mt-4 rounded-xl border border-teal-500/20 bg-teal-500/[0.04] p-4" data-testid={`investor-map-${companyId}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-teal-300">
          <Map className="h-3.5 w-3.5" /> Investor Map · {data.funders.length} mandate-matched funder{data.funders.length === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2" data-testid={`intro-target-${companyId}`}>
          <span className="text-[11px] font-semibold text-neutral-400">Warm intros: <b className="text-neutral-200">{made}</b> of {target_min}–{target_max} target</span>
          <div className="h-1.5 w-28 overflow-hidden rounded-full bg-neutral-800">
            <div className={"h-full rounded-full " + (made >= target_min ? "bg-emerald-400" : "bg-amber-400")} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      {data.funders.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500">No approved funders currently match this company's profile — matches appear as funders confirm mandates.</p>
      ) : (
        <div className="mt-3 divide-y divide-neutral-800/80">
          {data.funders.map((f) => (
            <div key={f.funder_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={`investor-row-${f.funder_id}`}>
              <span className={"inline-flex h-7 w-11 items-center justify-center rounded-md text-xs font-extrabold " +
                (f.compatibility >= 70 ? "bg-emerald-500/15 text-emerald-300" : f.compatibility >= 50 ? "bg-amber-500/15 text-amber-300" : "bg-neutral-800 text-neutral-400")}>
                {f.compatibility}%
              </span>
              <span className="text-sm font-semibold text-neutral-200">{f.name}</span>
              {f.investor_type && <span className="rounded-full border border-neutral-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">{f.investor_type.replace("_", " ")}</span>}
              <span className="text-xs text-neutral-500">
                {[
                  (f.sectors || []).slice(0, 2).join(", "),
                  (f.countries || []).slice(0, 2).join(", "),
                  f.ticket_min || f.ticket_max ? `${fmtTicket(f.ticket_min) || "$0"}–${fmtTicket(f.ticket_max) || "open"}` : null,
                ].filter(Boolean).join(" · ")}
              </span>
              {f.intro_logged && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-emerald-400">Intro logged ✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
